import React from 'react';
import { COLORS } from '../utils/constants';

// ─── Named skeleton primitives ────────────────────────────────────────────────

const shimmerCard = {
  borderRadius: 12,
  background: `linear-gradient(
    90deg,
    rgba(255,255,255,0.04) 25%,
    rgba(255,255,255,0.10) 50%,
    rgba(255,255,255,0.04) 75%
  )`,
  backgroundSize: '600px 100%',
  animation: 'car-alert-shimmer 1.4s ease-in-out infinite',
  border: '1px solid rgba(255,255,255,0.06)',
};

/** A single card-shaped skeleton placeholder */
export const SkeletonCard = () => (
  <div style={{ ...shimmerCard, padding: 16 }}>
    <div style={{ ...shimmerCard, height: 22, borderRadius: 6, marginBottom: 12 }} />
    <div style={{ ...shimmerCard, height: 16, borderRadius: 4, marginBottom: 8 }} />
    <div style={{ ...shimmerCard, height: 16, borderRadius: 4, width: '66%' }} />
  </div>
);

/** A table of skeleton rows */
export const SkeletonTable = ({ rows = 5 }) => (
  <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
    {Array.from({ length: rows }).map((_, i) => (
      <div key={`skeleton-row-${i}`} style={{ ...shimmerCard, height: 48 }} />
    ))}
  </div>
);

/** A circular avatar skeleton */
export const SkeletonAvatar = () => (
  <div style={{ ...shimmerCard, width: 48, height: 48, borderRadius: '50%' }} />
);

/** A vertical list of SkeletonCard items */
export const SkeletonList = ({ count = 3 }) => (
  <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
    {Array.from({ length: count }).map((_, i) => (
      <SkeletonCard key={`skeleton-card-${i}`} />
    ))}
  </div>
);

// ─── Full-page skeleton (default export, used as Suspense fallback) ───────────

/**
 * LoadingSkeleton — shown as the Suspense fallback while lazy-loaded route
 * bundles are downloading. Uses animated skeleton bars instead of a plain
 * white spinner so the perceived performance feels faster.
 */
const LoadingSkeleton = () => (
  <div style={styles.container}>
    <style>{KEYFRAMES}</style>

    {/* Top navigation bar skeleton */}
    <div style={styles.navBar}>
      <div style={{ ...styles.shimmer, width: 32, height: 32, borderRadius: 8 }} />
      <div style={{ ...styles.shimmer, width: 120, height: 20, borderRadius: 4 }} />
      <div style={{ ...styles.shimmer, width: 32, height: 32, borderRadius: '50%' }} />
    </div>

    {/* Main content area */}
    <div style={styles.content}>
      {/* Hero / header card */}
      <div style={{ ...styles.card, height: 120 }} />

      {/* Two stat cards side by side */}
      <div style={styles.row}>
        <div style={{ ...styles.card, flex: 1, height: 80 }} />
        <div style={{ ...styles.card, flex: 1, height: 80 }} />
      </div>

      {/* List items */}
      {[1, 2, 3].map((i) => (
        <div key={i} style={{ ...styles.card, height: 64 }} />
      ))}
    </div>
  </div>
);

const KEYFRAMES = `
  @keyframes car-alert-shimmer {
    0%   { background-position: -600px 0; }
    100% { background-position: 600px 0; }
  }
`;

const SHIMMER_BG = `linear-gradient(
  90deg,
  rgba(255,255,255,0.04) 25%,
  rgba(255,255,255,0.10) 50%,
  rgba(255,255,255,0.04) 75%
)`;

const styles = {
  container: {
    minHeight: '100vh',
    background: 'linear-gradient(135deg, #0a0e1a 0%, #1a2035 100%)',
    display: 'flex',
    flexDirection: 'column',
  },
  navBar: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: '12px 20px',
    borderBottom: `1px solid ${COLORS.border || 'rgba(255,255,255,0.08)'}`,
  },
  content: {
    padding: 20,
    display: 'flex',
    flexDirection: 'column',
    gap: 12,
  },
  row: {
    display: 'flex',
    gap: 12,
  },
  card: {
    borderRadius: 12,
    background: SHIMMER_BG,
    backgroundSize: '600px 100%',
    animation: 'car-alert-shimmer 1.4s ease-in-out infinite',
    border: `1px solid rgba(255,255,255,0.06)`,
  },
  shimmer: {
    background: SHIMMER_BG,
    backgroundSize: '600px 100%',
    animation: 'car-alert-shimmer 1.4s ease-in-out infinite',
  },
};

export default LoadingSkeleton;
